import { motion } from "framer-motion";
import { MessageCircle, Phone, Briefcase, Home, MapPin, Truck } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useSiteImage, useContact } from "../../hooks/useSettings";

const WA_MSG_AR =
    "السلام عليكم، أحتاج خدمة تغيير الإطارات المتنقلة من شركة إطار الثريا لخدمات السيارات — موقعي: ";
const WA_MSG_EN =
    "Hello, I need Al Thuraya Tire Auto Services Co mobile tire-changing service — my location: ";

const PLACE_ICONS = [Briefcase, Home, MapPin];

export const MobileService = () => {
    const { t, tr, lang, fs } = useLang();
    const vanImg = useSiteImage("img_mobile", "/assets/shop/mobile-service.jpg");

    const { mobile, callUrl, waUrl } = useContact();
    return (
        <section
            id="mobile-service"
            data-testid="mobile-service-section"
            className="relative py-24 sm:py-28 overflow-hidden border-b border-white/5 bg-[#0C1634]"
        >
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-red-600/15 blur-[120px]" />
            <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#253C96]/25 blur-[130px]" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="inline-flex items-center gap-2 bg-red-600/15 border border-red-500/40 rounded-full px-4 py-1.5 mb-6">
                        <Truck className="w-4 h-4 text-red-400" />
                        <span className={`text-xs font-extrabold text-red-300 ${fs("mob_badge")}`}>
                            {t("mob_badge")}
                        </span>
                    </div>

                    <h2 className={`font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-[1.2] text-white ${fs("mob_t1")}`}>
                        {t("mob_t1")}
                        <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-l from-red-400 via-red-500 to-amber-400">
                            {t("mob_t2")}
                        </span>
                    </h2>

                    <p className={`mt-5 text-base sm:text-lg text-slate-300 leading-relaxed ${fs("mob_desc")}`}>
                        {t("mob_desc")}
                    </p>

                    <div className="mt-7 flex flex-wrap gap-3">
                        {tr.mob_places.map((label, i) => {
                            const Icon = PLACE_ICONS[i];
                            return (
                                <motion.span
                                    key={label}
                                    initial={{ opacity: 0, scale: 0.85 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.3 + i * 0.12, duration: 0.5 }}
                                    data-testid={`mobile-service-place-${i + 1}`}
                                    className={`flex items-center gap-2 bg-white/5 backdrop-blur border border-white/15 rounded-full px-5 py-2.5 text-sm font-bold text-slate-200 ${fs("mob_places")}`}
                                >
                                    <Icon className="w-4 h-4 text-amber-400" />
                                    {label}
                                </motion.span>
                            );
                        })}
                    </div>

                    <div className="mt-8 flex flex-wrap gap-4">
                        <a
                            href={waUrl(lang === "ar" ? WA_MSG_AR : WA_MSG_EN)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="mobile-service-whatsapp-button"
                            className={`flex items-center gap-2.5 bg-emerald-500 text-[#0A1228] font-extrabold text-sm sm:text-base rounded-full px-7 py-3.5 hover:bg-emerald-400 transition-colors duration-300 shadow-[0_0_35px_rgba(16,185,129,0.25)] ${fs("mob_cta")}`}
                        >
                            <MessageCircle className="w-5 h-5" />
                            {t("mob_cta")}
                        </a>
                        <a
                            href={callUrl}
                            data-testid="mobile-service-call-button"
                            className="flex items-center gap-2.5 border border-white/25 text-white font-bold text-sm sm:text-base rounded-full px-7 py-3.5 hover:border-amber-400/60 hover:text-amber-400 transition-colors duration-300"
                            dir="ltr"
                        >
                            <Phone className="w-5 h-5" />
                            {mobile}
                        </a>
                    </div>
                </motion.div>

                <motion.figure
                    initial={{ opacity: 0, scale: 0.94 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.55)] group"
                    data-testid="mobile-service-image"
                >
                    <img
                        src={vanImg}
                        alt={t("mob_img_cap")}
                        loading="lazy"
                        className="w-full h-[340px] sm:h-[430px] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1228]/90 via-transparent to-transparent" />
                    <figcaption className="absolute bottom-4 right-5 left-5 flex items-center justify-between">
                        <span className={`font-display font-bold text-white text-sm ${fs("mob_img_cap")}`}>
                            {t("mob_img_cap")}
                        </span>
                        <span className="font-mono-spec text-[10px] tracking-widest text-amber-400" dir="ltr">
                            MERO MOBILE
                        </span>
                    </figcaption>
                </motion.figure>
            </div>
        </section>
    );
};
